import { Button, Card, Chip, EmptyState, Input, ListRow, Segmented } from '@brain/ui'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Copy, Plus, User, UserPlus, Users } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AttendeesDialog } from '../../components/people/AttendeesDialog.tsx'
import { DuplicatesDialog } from '../../components/people/MergeDialogs.tsx'
import { PersonDialog } from '../../components/people/PersonDialog.tsx'
import { relativeDay } from '../../lib/list-date.ts'
import { attendeesQuery, type PeopleSort, peopleSortedQuery } from '../../lib/people-m4.ts'

export const Route = createFileRoute('/_app/personen/')({ component: PeoplePage })

function PeoplePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [sort, setSort] = useState<PeopleSort>('name')
  const [creating, setCreating] = useState(false)
  const [dialog, setDialog] = useState<'attendees' | 'duplicates' | null>(null)
  const people = useQuery(peopleSortedQuery(q, sort))
  const attendees = useQuery(attendeesQuery())
  const items = people.data ?? []
  const due = items.filter((p) => p.contactDue).length
  return (
    <div className="min-w-0 flex-1 overflow-y-auto">
      <div className="mx-auto flex max-w-3xl flex-col gap-4 px-4 py-4 md:px-8 md:py-6">
        <header data-tauri-drag-region className="flex flex-wrap items-center gap-3">
          <h1 className="flex-1 text-[22px] text-text-1">{t('nav.people')}</h1>
          <Link to="/zusagen" className="font-medium text-[13px] text-pop-fg">
            {t('nav.commitments')}
          </Link>
          <Button size={32} variant="primary" onClick={() => setCreating(true)}>
            <Plus size={14} />
            {t('people.new')}
          </Button>
        </header>
        {(attendees.data?.length ?? 0) > 0 && (
          <Card className="flex flex-wrap items-center gap-3 p-4">
            <UserPlus size={16} strokeWidth={1.9} className="text-pop-fg" />
            <p className="min-w-0 flex-1 text-[13px] text-text-1">
              {t('crm.attendees.banner', { count: attendees.data?.length ?? 0 })}
            </p>
            <Button size={32} variant="secondary" onClick={() => setDialog('attendees')}>
              {t('crm.attendees.review')}
            </Button>
          </Card>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <Input
            className="min-w-48 flex-1"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('people.search')}
            aria-label={t('people.search')}
          />
          <Segmented
            aria-label={t('crm.sort.label')}
            value={sort}
            onValueChange={(v) => setSort(v as PeopleSort)}
            options={[
              { value: 'name', label: t('crm.sort.name') },
              { value: 'contact', label: t('crm.sort.contact') },
              { value: 'due', label: due ? t('crm.sort.dueCount', { count: due }) : t('crm.sort.due') },
            ]}
          />
          <Button size={32} variant="ghost" onClick={() => setDialog('duplicates')}>
            <Copy size={14} />
            {t('crm.duplicates.open')}
          </Button>
        </div>
        {people.isPending ? (
          <p className="text-[13px] text-meta">{t('common.loading')}</p>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Users size={20} strokeWidth={1.9} />}
            title={t('people.empty')}
            description={t('people.emptyText')}
          />
        ) : (
          <Card className="p-2">
            {items.map((p) => (
              <Link key={p.id} to="/personen/$personId" params={{ personId: p.id }} className="block">
                <ListRow
                  leading={<User size={16} strokeWidth={1.9} />}
                  title={p.name}
                  subtitle={[
                    p.company,
                    p.role,
                    p.lastContactAt
                      ? t('people.lastContact', { date: relativeDay(p.lastContactAt) })
                      : p.emails[0],
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                  trailing={
                    <span className="flex items-center gap-2">
                      {p.contactDue && <Chip tone="apricot">{t('crm.contactDue')}</Chip>}
                      {p.openCommitments.iOwe + p.openCommitments.owedToMe > 0 && (
                        <span className="text-[11px] text-meta">
                          {t('people.openCommitments', {
                            iOwe: p.openCommitments.iOwe,
                            owed: p.openCommitments.owedToMe,
                          })}
                        </span>
                      )}
                    </span>
                  }
                />
              </Link>
            ))}
          </Card>
        )}
        {creating && (
          <PersonDialog
            person={null}
            onClose={() => setCreating(false)}
            onSaved={(id) => void navigate({ to: '/personen/$personId', params: { personId: id } })}
          />
        )}
        {dialog === 'attendees' && <AttendeesDialog onClose={() => setDialog(null)} />}
        {dialog === 'duplicates' && <DuplicatesDialog onClose={() => setDialog(null)} />}
      </div>
    </div>
  )
}
